import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, ListOrdered, Square, RotateCcw, Trash2, Cloud, Monitor, AlertTriangle, CheckCircle2, Loader2
} from 'lucide-react';
import { readReferralCsv } from '../../services/referralCsv';

/**
 * CSV Classification Queue (admin).
 *
 * Upload SEMrush referring-domain exports and have every domain classified
 * AHEAD of any brand add. Nothing is written to brands or affiliates: the
 * verdicts only go into the classification cache (classified_urls for iGaming,
 * forex_classified_urls for Trading). Domains already classified are skipped;
 * the rest are judged by Claude in the cloud or by the local Claude worker.
 * Uploads run one at a time, oldest first — the next starts when one finishes.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const BASE = `${API_URL}/api/admin/classify-imports`;

const POLL_MS = 5000;

const request = async (path, options = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options
  });
  return response.json();
};

// The same columns the add-brand pipeline reads a CSV's domain from.
const domainOf = (row) => row.domain || row.backlink_url || row.url || row['referring domain'] || '';

const LIST_TYPES = [
  { id: 'igaming', label: 'iGaming affiliates' },
  { id: 'trading', label: 'Trading affiliates' }
];

const MODES = [
  { id: 'cloud', label: 'Cloud (Claude)', hint: 'Fast — spends Zyte + Claude credits', icon: Cloud },
  { id: 'local', label: 'Local (Claude)', hint: 'Free — a desktop running the worker does it', icon: Monitor }
];

const STATUS_STYLE = {
  queued: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  stopped: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
};

const fmt = (n) => Number(n || 0).toLocaleString();

const formatWhen = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const Choice = ({ options, value, onChange, disabledIds = [] }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const Icon = opt.icon;
      const disabled = disabledIds.includes(opt.id);
      const active = value === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.id)}
          className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
            active
              ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {Icon && <Icon className="mt-0.5 h-4 w-4 text-gray-500 dark:text-slate-400" />}
          <span>
            <span className="block font-medium text-gray-900 dark:text-slate-100">{opt.label}</span>
            {opt.hint && <span className="block text-xs text-gray-500 dark:text-slate-400">{opt.hint}</span>}
          </span>
        </button>
      );
    })}
  </div>
);

const Progress = ({ item }) => {
  const toClassify = item.to_classify || 0;
  const pct = toClassify > 0 ? Math.min(100, Math.round((item.processed / toClassify) * 100)) : 0;
  if (item.status === 'queued') return <span className="text-gray-400">Waiting</span>;
  if (item.status === 'running' && toClassify === 0 && !item.processed) return <span className="text-gray-400">Starting…</span>;
  return (
    <div className="min-w-[140px]">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
        <div
          className={`h-full ${item.status === 'running' ? 'bg-blue-500' : 'bg-gray-400 dark:bg-slate-500'}`}
          style={{ width: `${item.status === 'done' ? 100 : pct}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">
        {fmt(item.processed)} / {fmt(toClassify)} classified
      </div>
    </div>
  );
};

const ClassifyImport = () => {
  const [listType, setListType] = useState('trading');
  const [mode, setMode] = useState('cloud');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [imports, setImports] = useState([]);
  const [localWorkers, setLocalWorkers] = useState(0);
  const [loading, setLoading] = useState(true);
  const fileInput = useRef(null);

  // Local (Claude) only carries the Trading prompt today.
  useEffect(() => {
    if (listType === 'igaming' && mode === 'local') setMode('cloud');
  }, [listType, mode]);

  const load = useCallback(async () => {
    try {
      const data = await request('');
      if (data.success) {
        setImports(data.imports || []);
        setLocalWorkers(data.localWorkers || 0);
      }
    } catch (err) {
      console.error('Failed to load classify queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll only while something is waiting or running.
  const active = imports.some((i) => i.status === 'queued' || i.status === 'running');
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [active, load]);

  const handleQueue = async () => {
    if (!files.length) return;
    setUploading(true);
    setMessage(null);
    const results = [];
    // One queue entry per file, in the order they were picked.
    for (const file of files) {
      const { rows, error } = await readReferralCsv(file);
      if (error) {
        results.push({ ok: false, text: `${file.name}: ${error}` });
        continue;
      }
      const domains = rows.map(domainOf).filter(Boolean);
      try {
        const data = await request('', {
          method: 'POST',
          body: JSON.stringify({ fileName: file.name, listType, classifyMode: mode, domains })
        });
        results.push(data.success
          ? { ok: true, text: `${file.name}: queued ${fmt(data.import.total)} domains` }
          : { ok: false, text: `${file.name}: ${data.message || 'failed to queue'}` });
      } catch (err) {
        results.push({ ok: false, text: `${file.name}: ${err.message}` });
      }
    }
    setMessage(results);
    setFiles([]);
    if (fileInput.current) fileInput.current.value = '';
    setUploading(false);
    load();
  };

  const act = async (id, path, method = 'POST') => {
    const data = await request(`/${id}${path}`, { method });
    if (!data.success) setMessage([{ ok: false, text: data.message || 'Action failed' }]);
    load();
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl">
        <div className="mb-1 flex items-center gap-3">
          <ListOrdered className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">CSV Classification Queue</h1>
        </div>
        <p className="mb-6 text-gray-600 dark:text-slate-400">
          Upload SEMrush referring-domain CSVs to classify their domains ahead of time. Domains that are already
          classified are skipped; new ones are classified and saved to the classification table only — no brands,
          no affiliates, no SEMrush calls. The next brand add with these domains gets the verdicts for free.
        </p>

        {/* ---- Upload ---- */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <div className="mb-2 font-medium text-gray-800 dark:text-slate-200">List type</div>
              <Choice options={LIST_TYPES} value={listType} onChange={setListType} />
            </div>
            <div>
              <div className="mb-2 font-medium text-gray-800 dark:text-slate-200">Classify with</div>
              <Choice
                options={MODES}
                value={mode}
                onChange={setMode}
                disabledIds={listType === 'igaming' ? ['local'] : []}
              />
              {listType === 'igaming' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  Local (Claude) is only available for Trading lists for now.
                </p>
              )}
              {mode === 'local' && (
                <p className={`mt-2 flex items-center gap-1 text-xs ${localWorkers ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {localWorkers ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  {localWorkers
                    ? `${localWorkers} local worker${localWorkers > 1 ? 's' : ''} online`
                    : 'No local worker online — start `npm run worker` in mars-local-classify, or the import fails after 30 minutes'}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              type="file"
              accept=".csv"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-gray-200 dark:text-slate-300 dark:file:bg-slate-800 dark:file:text-slate-200"
            />
            <button
              type="button"
              onClick={handleQueue}
              disabled={!files.length || uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {files.length > 1 ? `Add ${files.length} CSVs to queue` : 'Add to queue'}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
            CSV needs a "Domain" column (a SEMrush referring-domains export works as-is). Pick several files to queue them all.
          </p>

          {message && (
            <ul className="mt-3 space-y-1">
              {message.map((m, i) => (
                <li key={i} className={`text-sm ${m.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {m.text}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ---- Queue ---- */}
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="min-w-full text-left">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Domains</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Result</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
              )}
              {!loading && imports.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500">No CSVs queued yet.</td></tr>
              )}
              {imports.map((item) => (
                <tr key={item.id} className="align-top text-gray-800 dark:text-slate-200">
                  <td className="px-4 py-3 text-gray-500">{item.id}</td>
                  <td className="max-w-[220px] truncate px-4 py-3" title={item.file_name}>{item.file_name}</td>
                  <td className="px-4 py-3">
                    <div>{item.list_type === 'trading' ? 'Trading' : 'iGaming'}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {item.classify_mode === 'local' ? 'Local (Claude)' : 'Cloud (Claude)'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[item.status] || STATUS_STYLE.queued}`}>
                      {item.status === 'running' && item.stop_requested ? 'stopping' : item.status}
                    </span>
                    {item.error && (
                      <div className="mt-1 max-w-[260px] text-xs text-red-600 dark:text-red-400">{item.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>{fmt(item.total)} total</div>
                    {item.status !== 'queued' && (
                      <div className="text-xs text-gray-500 dark:text-slate-400">
                        {fmt(item.already_classified)} already done · {fmt(item.to_classify)} new
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><Progress item={item} /></td>
                  <td className="px-4 py-3 text-xs">
                    {item.status === 'done' || (['stopped', 'failed'].includes(item.status) && item.saved > 0) ? (
                      <>
                        <div className="font-medium text-gray-800 dark:text-slate-200">{fmt(item.saved)} saved</div>
                        <div className="text-green-600 dark:text-green-400">{fmt(item.affiliates)} affiliate</div>
                        <div className="text-gray-500 dark:text-slate-400">
                          {fmt(item.brands)} brand · {fmt(item.not_affiliates)} not affiliate
                        </div>
                        {item.to_classify > item.saved && (
                          <div className="text-amber-600 dark:text-amber-400">
                            {fmt(item.to_classify - item.saved)} unjudged (retry later)
                          </div>
                        )}
                      </>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-slate-400">{formatWhen(item.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {(item.status === 'queued' || item.status === 'running') && !item.stop_requested && (
                        <button type="button" title="Stop" onClick={() => act(item.id, '/stop')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800">
                          <Square className="h-4 w-4" />
                        </button>
                      )}
                      {(item.status === 'failed' || item.status === 'stopped') && (
                        <button type="button" title="Retry" onClick={() => act(item.id, '/retry')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-slate-800">
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      {item.status !== 'running' && (
                        <button type="button" title="Remove" onClick={() => act(item.id, '', 'DELETE')}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-slate-800">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClassifyImport;
